import { createContext, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { CDN_URL } from '@/config/cdn';

const GONG_URL = `${CDN_URL}/audio/sfx/gong.mp3`;
const RECENT_COMMANDS_KEY = 'admin-command-palette-recent';
const MAX_RECENT = 5;

// Pre-load audio for instant playback
let gongAudio = null;
if (typeof window !== 'undefined') {
  gongAudio = new Audio(GONG_URL);
  gongAudio.volume = 0.3;
}

const playGongSound = () => {
  try {
    if (gongAudio) {
      gongAudio.currentTime = 0;
      gongAudio.play().catch(() => {});
    }
  } catch {
    // Silently fail if audio playback fails
  }
};

export const CommandPaletteContext = createContext(null);

export const CommandPaletteProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [recentCommands, setRecentCommands] = useState(() => {
    try {
      const stored = localStorage.getItem(RECENT_COMMANDS_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const navigate = useNavigate();

  // Play gong when opening
  const openPalette = useCallback(() => {
    playGongSound();
    setIsOpen(true);
  }, []);

  const closePalette = useCallback(() => {
    setIsOpen(false);
  }, []);

  const togglePalette = useCallback(() => {
    if (!isOpen) {
      openPalette();
    } else {
      closePalette();
    }
  }, [isOpen, openPalette, closePalette]);

  // Add command to recent list (only store serializable data - just the ID)
  const addToRecent = useCallback((command) => {
    setRecentCommands((prev) => {
      // Remove if already exists
      const filtered = prev.filter((c) => c.id !== command.id);
      // Only store the ID - the full command will be looked up when rendering
      const recentEntry = { id: command.id };
      // Add to front
      const updated = [recentEntry, ...filtered].slice(0, MAX_RECENT);
      // Persist
      try {
        localStorage.setItem(RECENT_COMMANDS_KEY, JSON.stringify(updated));
      } catch {
        // Ignore storage errors
      }
      return updated;
    });
  }, []);

  // Execute a command
  const executeCommand = useCallback(
    (command) => {
      closePalette();
      addToRecent(command);

      if (command.action) {
        command.action();
      } else if (command.path) {
        navigate(command.path);
      }
    },
    [closePalette, addToRecent, navigate]
  );

  // Keyboard shortcut: Cmd+K / Ctrl+K
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        togglePalette();
      }
      // Also close on Escape (handled by dialog, but just in case)
      if (e.key === 'Escape' && isOpen) {
        closePalette();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, togglePalette, closePalette]);

  return (
    <CommandPaletteContext.Provider
      value={{
        isOpen,
        openPalette,
        closePalette,
        togglePalette,
        recentCommands,
        executeCommand,
      }}
    >
      {children}
    </CommandPaletteContext.Provider>
  );
};
